import { useEffect, useRef, useState } from "react";
import { styled } from "styled-components";

import { ExpandableContent } from "common/components";
import { breakpoints, createScrollbarStyle } from "common/styles";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { KeyIssuesSection } from "domains/createMapDetail/components/keyIssueList/KeyIssuesSection";
import { ShareSettings } from "domains/createShareSettings/components/ShareSettings";
import { CreateCustomContentSection } from "domains/customContentAndSources/components/CreateCustomContentSection";
import { toggleCustomKeyIssueForm } from "domains/customKeyIssue/actions";
import { activateModal } from "domains/modal/actions";
import { TopicDetailInfo } from "domains/topicDetail/components/TopicDetailInfo";
import { TopicFeed } from "domains/topicDetail/components/TopicFeed";
import { TopicKeyIssueSection } from "domains/topicDetail/components/TopicKeyIssueSection";
import { getTmEditMode, getTmSelectedOuterNode } from "domains/transformationMap/selectors";
import { useMedia } from "domains/viewport/useMedia";
import { getCreateMapShareSettingsAccess } from "entities/permissions/selectors";
import { getTopic } from "entities/topics/selectors";

const StyledContainer = styled.div`
  box-sizing: border-box;
  padding: 0 0 1rem 0;
  width: 100%;

  ${createScrollbarStyle()}

  @media ${breakpoints.medium} {
    padding: 0 0 2rem 0;
  }

  @media ${breakpoints.large} {
    overflow-y: auto;
    padding: 2rem 0 2rem 5px;
  }

  @media ${breakpoints.extraLarge} {
    padding: 3rem 0 3rem 5px;
  }
`;

const StyledKeyIssueContainer = styled.div`
  display: block;

  @media ${breakpoints.large} {
    display: none;
  }
`;

const StyledTopContainer = styled.div`
  margin: 0;

  @media ${breakpoints.large} {
    border-radius: 5px;
    box-shadow: ${({ theme }) => theme.shadows.box};
    margin: 0 2rem 0 0;
  }

  @media ${breakpoints.extraLarge} {
    margin: 0 3rem 0 0;
  }
`;

const StyledWrapper = styled.div`
  @media ${breakpoints.large} {
    padding-bottom: 1.5rem;
  }
`;

const StyledInnerContainer = styled.div`
  margin: 1rem 1rem 0;
  overflow-x: hidden;

  @media ${breakpoints.medium} {
    margin: 2rem 2rem 0;
  }

  @media ${breakpoints.large} {
    margin: 0 2rem 0 0;
  }

  @media ${breakpoints.extraLarge} {
    margin: 0 3rem 0 0;
  }
`;

type TopicDetailPanelProps = { topicId?: string };

export const TopicDetailPanel = ({ topicId }: TopicDetailPanelProps) => {
  const dispatch = useDispatch();
  const showKeyIssuesSection = useMedia({ small: true, medium: true, large: true, extraLarge: true }, false);
  const topic = useSelector(getTopic(topicId));
  const accessToShareSettings = useSelector(getCreateMapShareSettingsAccess);
  const isEditing = useSelector(getTmEditMode);
  const selectedOuterNode = useSelector(getTmSelectedOuterNode);
  const [selectedSection, setSelectedSection] = useState<string | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const scrollToTop = () => {
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const onAddRemoveSelect = () => dispatch(activateModal({ id: "Dimensions" }));
  const onAddContentSelect = () => dispatch(activateModal({ id: "AddCustomContent" }));
  const onEditKeyIssueSelect = (id: string) => {
    dispatch(toggleCustomKeyIssueForm({ id, isActive: true }));
    dispatch(activateModal({ id: "AddCustomDimension" }));
  };

  useEffect(() => {
    scrollToTop();
  }, [selectedSection, selectedOuterNode?.id, topicId]);

  useEffect(() => {
    if (selectedSection) {
      setSelectedSection(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topic?.id]);

  return (
    <StyledContainer id="topic-content" ref={containerRef} tabIndex={0}>
      <ExpandableContent isActive={!selectedSection}>
        {(topic?.isTMLoaded === true || isEditing) && (
          <StyledWrapper>
            <StyledTopContainer>
              <TopicDetailInfo topic={topic} />
            </StyledTopContainer>
          </StyledWrapper>
        )}
      </ExpandableContent>
      {isEditing ? (
        <StyledInnerContainer>
          {showKeyIssuesSection && (
            <KeyIssuesSection onAddRemoveSelect={onAddRemoveSelect} onEditKeyIssueSelect={onEditKeyIssueSelect} />
          )}
          {accessToShareSettings && isEditing && <ShareSettings />}
          {showKeyIssuesSection && topic?.id && (
            <CreateCustomContentSection topicId={topic.id} onAddContentSelect={onAddContentSelect} />
          )}
        </StyledInnerContainer>
      ) : (
        topicId &&
        topic?.isTMLoaded && (
          <>
            <StyledKeyIssueContainer>
              <TopicKeyIssueSection topicId={topicId} selectedSectionId={selectedSection} scrollToTop={scrollToTop} />
            </StyledKeyIssueContainer>
            <TopicFeed topicId={topicId} />
          </>
        )
      )}
    </StyledContainer>
  );
};
